import LogOutIcon from '@src/svgs/LogOutIcon';
import { contrastingTextColor } from '@src/utility/colorContrast';
import { initialsFromName } from '@src/utility/initials';
import { parsePrimaryBrandColor } from '@src/utility/showcaseBranding';
import { memo, type ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import type { Showcase } from '@src/types.ts/story';

type OrganizationAvatarTooltipProps = {
  email?: string;
  onLogOut: () => void;
  topChildren?: ReactNode;
  bottomChildren?: ReactNode;
  /** Compact mode: render avatar+name+email header, restyle action rows. */
  compact?: boolean;
  album?: Showcase | null;
  username?: string | null;
};

/**
 * PLAT-12089 — the account menu is one grid: every section (profile header,
 * Switch Projects label, project search, project rows, Log out) is inset by
 * MENU_ROW_INSET from the card's padding, so their left edges line up, and
 * every glyph sits in a MenuIcon slot of one size drawn in currentColor.
 */
export const MENU_ROW_INSET = '10px';
export const MENU_ICON_SIZE = 16;

function OrganizationAvatarTooltip({
  onLogOut,
  email,
  topChildren,
  bottomChildren,
  compact = false,
  album,
  username,
}: OrganizationAvatarTooltipProps) {
  const [logOutHover, setLogOutHover] = useState(false);

  if (compact) {
    const brandColor = parsePrimaryBrandColor(album?.branding?.primaryColors);
    const logoUrl = album?.logo?.url || album?.mainImage?.url;
    const initials = initialsFromName(album?.title || username);
    const initialsBg = brandColor ?? 'var(--accent)';
    const initialsFg = brandColor
      ? contrastingTextColor(brandColor)
      : 'var(--gray-900)';

    return (
      <Wrapper $compact>
        <Header>
          <HeaderAvatar>
            {logoUrl ? (
              <img src={`${logoUrl}?w=64&h=64&fit=crop`} alt="" />
            ) : (
              <HeaderInitials $bg={initialsBg} $fg={initialsFg}>
                {initials}
              </HeaderInitials>
            )}
          </HeaderAvatar>
          <HeaderText>
            <HeaderName title={username ?? undefined}>
              {username || album?.title || 'Account'}
            </HeaderName>
            {email && <HeaderEmail title={email}>{email}</HeaderEmail>}
          </HeaderText>
        </Header>

        {topChildren && (
          <>
            <MenuDivider />
            {topChildren}
          </>
        )}

        <MenuDivider />

        <MenuRow onClick={() => onLogOut()}>
          <MenuIcon>
            <LogOutIcon fillColor="currentColor" />
          </MenuIcon>
          <MenuLabel>Log out</MenuLabel>
        </MenuRow>

        {bottomChildren}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {topChildren}
      <Section
        clickable
        onClick={() => onLogOut()}
        onMouseEnter={() => setLogOutHover(true)}
        onMouseLeave={() => setLogOutHover(false)}
      >
        <LogOutIcon fillColor={logOutHover ? 'var(--primary)' : undefined} />
        <SectionText color={logOutHover ? 'var(--primary)' : undefined}>
          Log Out
        </SectionText>
      </Section>
      {email && (
        <Section>
          <EmailSectionText title={email}>{email}</EmailSectionText>
        </Section>
      )}
      {bottomChildren}
    </Wrapper>
  );
}

export default memo(OrganizationAvatarTooltip);

export const Wrapper = styled.div<{ $compact?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  padding: ${p => (p.$compact ? 'var(--spacing-sm)' : '16px')};
  gap: ${p => (p.$compact ? '2px' : '16px')};

  /* The modal card's surface and border (dialogStyles DialogCard), with the
     floating-menu elevation: the menu reads as the same kind of layer as a
     dialog, one step above the sidebar it opens from. */
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--elevation-toast);

  width: ${p => (p.$compact ? '256px' : '197px')};
`;

export const Section = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
    `}
`;

export const SectionText = styled.div<{ color?: string }>`
  color: ${props => props.color || 'var(--text-secondary)'};
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

export const EmailSectionText = styled(SectionText)`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  margin-left: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 24px);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 6px ${MENU_ROW_INSET} 8px;
  min-width: 0;
`;

const HeaderAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const HeaderInitials = styled.div<{ $bg: string; $fg: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: 0.2px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const HeaderName = styled.span`
  color: var(--foreground);
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderEmail = styled.span`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: var(--font-weight-regular);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The one divider between sections: full width of the card's content. */
export const MenuDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: var(--spacing-xs) 0;
  flex-shrink: 0;
`;

/**
 * A menu row: 16px icon slot + label, the rounded-row hover of the shared
 * context menu (`--surface-elevated`, text to `--foreground`). `$static`
 * rows are section labels that do nothing and so show no hover.
 */
export const MenuRow = styled.div<{ $static?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-height: 32px;
  box-sizing: border-box;
  padding: 0 ${MENU_ROW_INSET};
  border-radius: var(--radius-button);
  color: var(--text-secondary);
  cursor: ${p => (p.$static ? 'default' : 'pointer')};
  transition:
    background var(--transition-fast),
    color var(--transition-fast);

  ${p =>
    !p.$static &&
    css`
      &:hover {
        background: var(--surface-elevated);
        color: var(--foreground);
      }
    `}
`;

export const MenuIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${MENU_ICON_SIZE}px;
  height: ${MENU_ICON_SIZE}px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const MenuLabel = styled.span`
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
